import type { SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export type ZyVersionFilterPluginState = {
    versionFilter: SelectedZyVersionFilter;
};

export const zyVersionFilterPluginKey = new PluginKey<ZyVersionFilterPluginState>('zyVersionFilter');

/**
 *
 * The active filter lives in {@link ZyVersion} extension storage (not document attrs).
 * `setVersionFilter` updates storage and dispatches a meta-only transaction tagged with
 * `zyVersionFilter`. This plugin copies the storage value into plugin state on those
 * transactions so `editor.state` changes without mutating the document.
 *
 * React consumers (`useEditorState`, node views, dropdown) read the filter via
 * `getEditorVersionFilter()` / `zyVersionFilterPluginKey`. ProseMirror plugins
 * (selection, delete) read storage directly through the `readVersionFilter` callback.
 *
 * @param readVersionFilter - Returns the current filter from extension storage.
 */
export function createZyVersionFilterPlugin(readVersionFilter: () => SelectedZyVersionFilter): Plugin {
    return new Plugin({
        key: zyVersionFilterPluginKey,
        state: {
            init() {
                return { versionFilter: readVersionFilter() ?? null };
            },
            apply(tr, value) {
                if (!tr.getMeta('zyVersionFilter')) {
                    return value;
                }
                return { versionFilter: readVersionFilter() ?? null };
            },
        },
    });
}
